/* Bettr Sauna — Cross-tab state coordination
 * --------------------------------------------------------------------
 * Phase 17 — three browser APIs working together:
 *
 *   BroadcastChannel — when one tab mutates the cart, every other
 *     open tab gets notified and refreshes its drawer display. Keeps
 *     the cart count + totals in sync across multiple shop tabs.
 *
 *   Web Locks — navigator.locks.request serializes cart mutations
 *     across tabs. Prevents the "user clicks + in tab A and + in tab
 *     B simultaneously, both read qty=1, both write qty=2, we lose a
 *     unit" race. The lock is held for the duration of each mutation
 *     and released automatically when the callback resolves.
 *
 *   IndexedDB — mirror the cart JSON locally so page loads can render
 *     the drawer instantly from cache while a background fetch refreshes.
 *     Expires after 1 hour or when cart:updated fires.
 *
 * Public API (attached to window.BettrCrossTab):
 *   .broadcast(type, detail)       — send to other tabs
 *   .onMessage(type, handler)      — subscribe, returns unsubscriber
 *   .withLock(name, fn)            — run fn under a named Web Lock
 *   .cacheCart(cart)               — write cart JSON to IndexedDB
 *   .readCart()                    — returns cached cart or null (<1h old)
 * -------------------------------------------------------------------- */

(function () {
  'use strict';

  const CHANNEL = 'bettr-cart';
  const DB_NAME = 'bettr-cart-cache';
  const DB_STORE = 'cart';
  const DB_VERSION = 1;
  const CACHE_TTL_MS = 60 * 60 * 1000;

  // ------------------------------------------------------------------
  // BroadcastChannel setup with fallback to storage event.
  // ------------------------------------------------------------------
  let channel = null;
  const listeners = new Map();

  if ('BroadcastChannel' in self) {
    try {
      channel = new BroadcastChannel(CHANNEL);
      channel.addEventListener('message', (ev) => dispatchIncoming(ev.data));
    } catch (_) { channel = null; }
  }

  // Fallback path for private-mode Safari / old browsers: localStorage
  // writes fire a storage event in other tabs (not the writing tab).
  if (!channel) {
    window.addEventListener('storage', (ev) => {
      if (ev.key !== CHANNEL || !ev.newValue) return;
      try { dispatchIncoming(JSON.parse(ev.newValue)); } catch (_) {}
    });
  }

  function dispatchIncoming(msg) {
    if (!msg || !msg.type) return;
    const handlers = listeners.get(msg.type);
    if (!handlers) return;
    handlers.forEach((h) => { try { h(msg.detail); } catch (_) {} });
  }

  function broadcast(type, detail) {
    const msg = { type, detail, from: Date.now() };
    if (channel) {
      try { channel.postMessage(msg); return; } catch (_) {}
    }
    try {
      localStorage.setItem(CHANNEL, JSON.stringify(msg));
      // Clear so next write with same payload still fires storage event.
      setTimeout(() => {
        try { localStorage.removeItem(CHANNEL); } catch (_) {}
      }, 50);
    } catch (_) {}
  }

  function onMessage(type, handler) {
    if (!listeners.has(type)) listeners.set(type, new Set());
    listeners.get(type).add(handler);
    return () => listeners.get(type).delete(handler);
  }

  // ------------------------------------------------------------------
  // Web Locks.
  // ------------------------------------------------------------------
  async function withLock(name, fn) {
    if (!('locks' in navigator) || typeof navigator.locks.request !== 'function') {
      return await fn();
    }
    return navigator.locks.request(name, { mode: 'exclusive' }, async () => await fn());
  }

  // ------------------------------------------------------------------
  // IndexedDB cart mirror. Single-key store ("current") keyed by a
  // constant so we always overwrite.
  // ------------------------------------------------------------------
  function openDB() {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in self)) return reject(new Error('no indexedDB'));
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(DB_STORE)) {
          db.createObjectStore(DB_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  async function cacheCart(cart) {
    if (!cart) return;
    try {
      const db = await openDB();
      const tx = db.transaction(DB_STORE, 'readwrite');
      tx.objectStore(DB_STORE).put({ cart, cachedAt: Date.now() }, 'current');
      return new Promise((res) => { tx.oncomplete = () => { db.close(); res(); }; tx.onerror = () => { db.close(); res(); }; });
    } catch (_) { /* quota / private mode — ignore */ }
  }

  async function readCart() {
    try {
      const db = await openDB();
      return new Promise((resolve) => {
        const tx = db.transaction(DB_STORE, 'readonly');
        const req = tx.objectStore(DB_STORE).get('current');
        req.onsuccess = () => {
          db.close();
          const rec = req.result;
          if (!rec || !rec.cart) return resolve(null);
          if (Date.now() - rec.cachedAt > CACHE_TTL_MS) return resolve(null);
          resolve(rec.cart);
        };
        req.onerror = () => { db.close(); resolve(null); };
      });
    } catch (_) { return null; }
  }

  // ------------------------------------------------------------------
  // Wire up: when this tab dispatches cart:updated (the existing cart
  // drawer does this after every refresh), echo to other tabs. When
  // another tab broadcasts, dispatch locally so the drawer listener
  // picks it up and refreshes.
  // ------------------------------------------------------------------
  document.addEventListener('cart:updated', function (ev) {
    // Don't re-broadcast if the event itself came from a cross-tab relay.
    if (ev && ev.detail && ev.detail.__fromBroadcast) return;
    broadcast('cart:updated', {});
    // Opportunistic cache refresh.
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.ok ? r.json() : null)
      .then((cart) => cart && cacheCart(cart))
      .catch(() => {});
  });

  document.addEventListener('cart:cleaned', function (ev) {
    if (ev && ev.detail && ev.detail.__fromBroadcast) return;
    broadcast('cart:cleaned', {});
  });

  onMessage('cart:updated', function () {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { __fromBroadcast: true }
    }));
  });
  onMessage('cart:cleaned', function () {
    document.dispatchEvent(new CustomEvent('cart:cleaned', {
      detail: { __fromBroadcast: true }
    }));
  });

  window.BettrCrossTab = { broadcast, onMessage, withLock, cacheCart, readCart };
})();
